import { APIError } from '../APIError';
import { SecurityInvestigationsEntry } from '../routes/dashboard/SecurityInvestigations';
import { TopCountriesEntry } from '../routes/dashboard/TopCountriesMap';
import { ConfigurationEntry } from '../types';

type RawSensorMetrics = Array<{
  id: string;
  obs: Array<{ x: number; y: number }>;
}>;

type DashboardMetricTypes = 'summary' | 'incidents' | 'sensors' | 'countries';

type IncidentResults = {
  ticketed: number;
  histogram: Array<SecurityInvestigationsEntry>;
  total: number;
};

type CountryFlowsResults = {
  countries: Array<TopCountriesEntry>;
};

type SummaryResults = {
  security_score: string;
  configuration: {
    score: number;
    areas: Array<ConfigurationEntry>;
  };
};

type SetDashboardLoadingAction = {
  readonly type: 'set_dashboard_loading';
  metric: DashboardMetricTypes;
};

type SetIncidentsDataAction = {
  readonly type: 'set_incidents_dashboard_data';
  readonly metrics: IncidentResults;
};

type SetIncidentsErrorAction = {
  readonly type: 'set_dashboard_incidents_error';
  readonly err: APIError | null;
};

type SetCountryFlowsDataAction = {
  readonly type: 'set_country_flows_dashboard_data';
  readonly metrics: CountryFlowsResults;
};

type SetCountryFlowsErrorAction = {
  readonly type: 'set_dashboard_country_flows_error';
  readonly err: APIError | null;
};

type SetSensorMetricsAction = {
  readonly type: 'set_sensor_metrics';
  readonly sensors: RawSensorMetrics;
};

type SetSensorMetricsErrorAction = {
  readonly type: 'set_sensor_metrics_error';
  readonly err: APIError | null;
};

type SetSummaryDataAction = {
  readonly type: 'set_summary_dashboard_data';
  readonly metrics: SummaryResults;
};

type SetSummaryErrorAction = {
  readonly type: 'set_dashboard_summary_error';
  readonly err: APIError | null;
};

export type DashboardAction =
  | SetIncidentsDataAction
  | SetIncidentsErrorAction
  | SetCountryFlowsDataAction
  | SetCountryFlowsErrorAction
  | SetSensorMetricsAction
  | SetSensorMetricsErrorAction
  | SetSummaryDataAction
  | SetSummaryErrorAction
  | SetDashboardLoadingAction;

export const DASHBOARD_ACTIONS = {
  SET_DASHBOARD_LOADING: (
    metric: DashboardMetricTypes,
  ): SetDashboardLoadingAction => ({
    type: 'set_dashboard_loading',
    metric,
  }),
  SET_INCIDENTS_DATA: (metrics: IncidentResults): SetIncidentsDataAction => ({
    type: 'set_incidents_dashboard_data',
    metrics,
  }),
  SET_INCIDENTS_ERROR: (err: APIError | null): SetIncidentsErrorAction => ({
    type: 'set_dashboard_incidents_error',
    err,
  }),
  SET_COUNTRY_FLOWS_DATA: (
    metrics: CountryFlowsResults,
  ): SetCountryFlowsDataAction => ({
    type: 'set_country_flows_dashboard_data',
    metrics,
  }),
  SET_COUNTRY_FLOWS_ERROR: (
    err: APIError | null,
  ): SetCountryFlowsErrorAction => ({
    type: 'set_dashboard_country_flows_error',
    err,
  }),
  SET_SENSOR_METRICS: (raw: {
    sensors: RawSensorMetrics;
  }): SetSensorMetricsAction => ({
    type: 'set_sensor_metrics',
    sensors: raw.sensors,
  }),
  SET_SENSOR_METRICS_ERROR: (
    err: APIError | null,
  ): SetSensorMetricsErrorAction => ({
    type: 'set_sensor_metrics_error',
    err,
  }),
  SET_SUMMARY_DATA: (metrics: SummaryResults): SetSummaryDataAction => ({
    type: 'set_summary_dashboard_data',
    metrics,
  }),
  SET_SUMMARY_ERROR: (err: APIError | null): SetSummaryErrorAction => ({
    type: 'set_dashboard_summary_error',
    err,
  }),
};
